import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarDays,
  Download,
  FileSignature,
  HelpCircle,
  UserRound,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { useContractDetailQuery } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { contractDetailTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";

const STATUS_LABELS: Record<string, string> = {
  signed: "ລົງນາມແລ້ວ",
  pending_signature: "ລໍຖ້າລົງນາມ",
  expired: "ຫມົດອາຍຸແລ້ວ",
  draft: "ຮ່າງ",
};

export function ContractDetailPage() {
  const { id } = useParams({ from: "/app/documents/contracts/$id" });
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-contract-detail-tour-completed");
  const { data, isLoading, isError } = useContractDetailQuery(id ?? "");

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <Header />
        <Main>
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate({ to: "/app/documents/contracts" })}
          >
            <ArrowLeft className="mr-2 size-4" />
            ກັບໄປໜ້າສັນຍາ
          </Button>
          <p className="text-muted-foreground">ບໍ່ພົບສັນຍາ ຫຼື ບໍ່ມີສິດເຂົ້າເບິ່ງ</p>
        </Main>
      </>
    );
  }

  const downloadUrl = resolveImageSrc(data.fileKey);
  const progress = getContractProgress(data.startDate, data.endDate);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                {data.title}
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
          </div>
          <div>
            <Button
              variant="ghost"
              className="-ms-2 mb-3"
              onClick={() => navigate({ to: "/app/documents/contracts" })}
            >
              <ArrowLeft className="mr-2 size-4" />
              ກັບຄືນ
            </Button>
          </div>
          {downloadUrl ? (
            <Button
              type="button"
              variant="default"
              className="border-black"
              onClick={() => {
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                link.download = data.title || "contract";
                link.click();
              }}
            >
              <Download className="mr-2 size-4" />
              ດາວໂຫລດ
            </Button>
          ) : null}
        </div>

        <div
          data-tourid="doc-contract-detail-content"
          className="grid gap-4 lg:grid-cols-3"
        >
          <Card className="xl:col-span-1">
            <CardContent className="space-y-3 p-5 py-0">
              <h3 className="font-semibold text-base">ຂໍ້ມູນທົ່ວໄປ</h3>
              {data.contractNumber ? (
                <div>
                  <p className="text-muted-foreground text-xs">ເລກທີສັນຍາ</p>
                  <p className="font-medium text-sm">{data.contractNumber}</p>
                </div>
              ) : null}
              <div>
                <p className="text-muted-foreground text-xs">ປະເພດສັນຍາ</p>
                <p className="font-medium text-sm">{data.contractType}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ສະຖານະ</p>
                <Badge variant="outline" className="mt-1">
                  {STATUS_LABELS[data.status] ?? data.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="xl:col-span-1">
            <CardContent className="space-y-3 p-5 py-0">
              <h3 className="font-semibold text-base">ລະຍະເວລາ & ເງື່ອນໄຂ</h3>
              <div className="flex items-start gap-2">
                <CalendarDays className="mt-0.5 size-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground text-xs">ວັນທີເລີ່ມ</p>
                  <p className="font-medium text-sm">
                    {data.startDate ? formatDate(data.startDate) : "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarDays className="mt-0.5 size-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground text-xs">ວັນທີໝົດອາຍຸ</p>
                  <p className="font-medium text-sm">
                    {data.endDate ? formatDate(data.endDate) : "—"}
                  </p>
                </div>
              </div>
              {progress.remainingDays != null ? (
                <div>
                  <p className="text-muted-foreground text-xs">ຈະໝົດອາຍຸໃນ</p>
                  <p className="font-medium text-sm">
                    {progress.remainingDays} ວັນ
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="xl:col-span-1">
            <CardContent className="space-y-3 p-5 py-0">
              <h3 className="font-semibold text-base">ການປະຕິບັດງານ</h3>
              <div className="flex items-start gap-2">
                <UserRound className="mt-0.5 size-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground text-xs">ຜູ້ກ່ຽວຂ້ອງ</p>
                  <p className="font-medium text-sm">
                    {data.employeeName ?? "—"}
                  </p>
                  {data.employeeEmail ? (
                    <p className="text-muted-foreground text-sm">
                      {data.employeeEmail}
                    </p>
                  ) : null}
                </div>
              </div>
              <div>
                <p className="mb-1 text-muted-foreground text-xs">
                  ຄວາມຄືບໜ້າສັນຍາ
                </p>
                <div className="h-2 w-full rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-black"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <p className="mt-1 text-right text-muted-foreground text-xs">
                  {progress.percent}%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4">
          <CardContent className="space-y-3 p-5 py-0">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex size-9 items-center justify-center rounded-lg bg-black/5">
                <FileSignature className="size-5 text-black dark:text-white" />
              </div>
              <h2 className="font-semibold text-base">
                ໝາຍເຫດ ແລະ ເງື່ອນໄຂເພີ່ມເຕີມ
              </h2>
            </div>
            {data.notes ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                <span className="font-medium">ໝາຍເຫດ:</span> {data.notes}
              </p>
            ) : null}
            {data.specialConditions ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                <span className="font-medium">ເງື່ອນໄຂພິເສດ:</span>{" "}
                {data.specialConditions}
              </p>
            ) : null}
            {!data.notes && !data.specialConditions ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນເພີ່ມເຕີມ</p>
            ) : null}
          </CardContent>
        </Card>
        <DocumentPortalTour
          steps={contractDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}

function getContractProgress(
  startDate: string | null,
  endDate: string | null,
): {
  percent: number;
  remainingDays: number | null;
} {
  if (!startDate || !endDate) return { percent: 0, remainingDays: null };
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return { percent: 0, remainingDays: null };
  }
  const total = end - start;
  const elapsed = Math.min(Math.max(now - start, 0), total);
  const remaining = Math.max(end - now, 0);
  return {
    percent: Math.round((elapsed / total) * 100),
    remainingDays: Math.ceil(remaining / (1000 * 60 * 60 * 24)),
  };
}
